// Story 7.33 (ADO-2026): the shelf-plan read client.
//
// The plan a shop orders its shelf from: the Format and Category options of the shop's plans,
// the plans in one Format and Category, and one plan. A plan's products are not read here: they
// are the ordinary catalogue listing under the `planogram` scope (`browse.client.ts`). Lock-step
// with `planogram.dto.ts` in the backend `search-and-discovery` contracts.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { ENTRY_PARAM, withEntry } from './catalogue-entry';

/** One shelf plan the shop can open. */
export interface Planogram {
  readonly planogramId: string;
  readonly name: string;
  readonly format: string;
  readonly category: string;
  /** The plan image; null when BWG has no image for it (the card shows the placeholder). */
  readonly imageUrl: string | null;
  readonly bays: number | null;
  readonly shelves: number | null;
}

/** The Format and Category options of the shop's plans. */
export interface PlanogramFilters {
  /** Whether the shop is offered plans at all: the Planogram entry shows only when so. */
  readonly available: boolean;
  readonly formats: readonly string[];
  readonly categories: readonly string[];
}

export const PLANOGRAM_FILTERS_PATH = `${API_PREFIX}/planogram-filters`;
export const PLANOGRAMS_PATH = `${API_PREFIX}/planograms`;

export async function fetchPlanogramFilters(
  entry: string,
  format: string | null,
): Promise<PlanogramFilters> {
  const params = new URLSearchParams({ [ENTRY_PARAM]: entry });
  if (format !== null && format !== '') params.set('format', format);
  const res = await apiFetch(`${PLANOGRAM_FILTERS_PATH}?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to load planogram filters (${res.status})`);
  return (await res.json()) as PlanogramFilters;
}

export async function fetchPlanograms(
  entry: string,
  format: string,
  category: string,
): Promise<readonly Planogram[]> {
  const params = new URLSearchParams({ [ENTRY_PARAM]: entry, format, category });
  const res = await apiFetch(`${PLANOGRAMS_PATH}?${params.toString()}`);
  if (!res.ok) throw new Error(`Failed to load planograms (${res.status})`);
  return (await res.json()) as readonly Planogram[];
}

/** One plan; null when it is not one of the shop's (404). Any other failure throws. */
export async function fetchPlanogram(
  entry: string,
  planogramId: string,
): Promise<Planogram | null> {
  const res = await apiFetch(
    withEntry(`${PLANOGRAMS_PATH}/${encodeURIComponent(planogramId)}`, entry),
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load planogram (${res.status})`);
  return (await res.json()) as Planogram;
}

/**
 * The plan card's layout label, as the Figma cards spell it ("1 Bay × 3 Shelves"): SL1.0's
 * "N Bay x M Shelf" with the plurals the design uses. Empty when the file name states no layout.
 */
export function planogramLayoutLabel(bays: number | null, shelves: number | null): string {
  if (bays === null || shelves === null) return '';
  return `${bays} ${bays === 1 ? 'Bay' : 'Bays'} × ${shelves} ${shelves === 1 ? 'Shelf' : 'Shelves'}`;
}

/** A Format or Category value as the selects show it (`cluster2` reads "Cluster 2"). */
export function planogramOptionLabel(value: string): string {
  const spaced = value.trim().replace(/([A-Za-z])([0-9])/g, '$1 $2');
  return spaced === '' ? spaced : `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
}
